/**
 * The desk's queue table: tabs, filters, rows, and bulk actions.
 * Used by the all-titles overview and (with a fixed `publication`) by a title workspace.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type MeResponse } from '../../api/client'
import { useDebounced } from '../../lib/useDebounced'
import { useConversationPage } from '../../components/support/useConversationPage'
import { Chip } from '../../components/support/QueueList'
import { TopicFilter } from '../../components/support/TopicFilter'
import {
  TITLE_LABELS, TITLE_ORDER, stateLabel, topicLabel, topicTone, waitingLabel, waitingTone,
  type DeskConversation, type DeskState, type StaffMember,
} from '../../lib/supportDesk'

const TABS: { key: DeskState; label: string }[] = [
  { key: 'to_review', label: 'To review' }, { key: 'waiting', label: 'Waiting' },
  { key: 'snoozed', label: 'Snoozed' }, { key: 'done', label: 'Done' },
]
const PAGE = 50
const TONE: Record<string, string> = { ok: 'text-paper-dim', soon: 'text-amber-glow', late: 'text-red-400' }
const SELECT = 'bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px]'

export function useDeskMe() {
  const me = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const staff = useQuery<StaffMember[]>({ queryKey: ['support', 'staff'], queryFn: () => api.get<StaffMember[]>('/api/support/staff'), staleTime: 60_000, retry: false })
  const canAct = me.data?.role === 'admin' || me.data?.role === 'operator'
  const isAdmin = me.data?.role === 'admin'
  return { staff: staff.data ?? [], canAct, isAdmin, meLoading: me.isLoading, meEmail: me.data?.email }
}

export function conversationHref(c: DeskConversation, publication?: string): string {
  return `/support/${c.publication ?? publication ?? 'none'}?c=${c.channel}-${c.message_id}`
}

type Props = {
  publication?: string
  initialTab?: DeskState
  mine?: boolean // the overview's "Assigned to me" button: filters by assignee=me
}

export function SupportQueue({ publication, initialTab = 'to_review', mine = false }: Props) {
  const qc = useQueryClient()
  const { staff, canAct } = useDeskMe()
  const [tab, setTab] = useState<DeskState>(initialTab)
  const [title, setTitle] = useState('')
  const [topic, setTopic] = useState('')
  const [owner, setOwner] = useState('')
  const [q, setQ] = useState('')
  const dq = useDebounced(q.trim(), 300)
  const [offset, setOffset] = useState(0)
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const pub = publication ?? title
  const assignee = mine ? 'me' : owner
  const params = new URLSearchParams({ state: tab, limit: String(PAGE), offset: String(offset) })
  if (pub) params.set('publication', pub)
  if (topic) params.set('topic', topic)
  if (assignee) params.set('assignee', assignee)
  if (dq) params.set('q', dq)
  // The topic chips count under every other filter, so each count is what clicking it lists.
  const topicFilters = new URLSearchParams({ state: tab })
  if (pub) topicFilters.set('publication', pub)
  if (assignee) topicFilters.set('assignee', assignee)
  if (dq) topicFilters.set('q', dq)

  const list = useConversationPage(params)
  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0
  // After a bulk action the page can come back empty; step back to the last page that has rows.
  useEffect(() => {
    if (list.data && list.data.items.length === 0 && list.data.total > 0 && offset > 0) {
      setOffset(Math.floor((list.data.total - 1) / PAGE) * PAGE)
    }
  }, [list.data, offset])
  const key = (c: DeskConversation) => `${c.channel}-${c.message_id}`

  const bulk = useMutation({
    mutationFn: (b: { action: string; assignee_user_id?: number | null; snoozed_until?: string }) =>
      api.post('/api/support/conversations/bulk', {
        ...b, items: [...picked].map(k => { const [channel, id] = k.split('-'); return { channel, id: Number(id) } }),
      }),
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['support'] }) },
    onError: () => { qc.invalidateQueries({ queryKey: ['support'] }) },
  })

  const reset = () => { setOffset(0); setPicked(new Set()) }
  useEffect(() => { setOffset(0); setPicked(new Set()) }, [dq, mine])
  const snooze = (days: number) =>
    bulk.mutate({ action: 'snooze', snoozed_until: new Date(Date.now() + days * 86_400_000).toISOString() })
  const allOn = items.length > 0 && items.every(c => picked.has(key(c)))

  return (
    <section className="space-y-4">
      <div role="tablist" className="flex gap-1 border-b border-ink-500">
        {TABS.map(t => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => { setTab(t.key); reset() }}
            className={`label px-4 py-2 border-b-2 ${tab === t.key ? 'border-amber-glow text-amber-glow' : 'border-transparent'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        {!publication && (
          <select aria-label="Title" className={SELECT} value={title} onChange={e => { setTitle(e.target.value); reset() }}>
            <option value="">All titles</option>
            {[...TITLE_ORDER, 'none'].map(t => <option key={t} value={t}>{TITLE_LABELS[t]}</option>)}
          </select>
        )}
        <select aria-label="Owner" className={SELECT} value={mine ? 'me' : owner}
          disabled={mine} onChange={e => { setOwner(e.target.value); reset() }}>
          <option value="">Any owner</option>
          {mine && <option value="me">Me</option>}
          {staff.map(s => <option key={s.id} value={s.id}>{s.email}</option>)}
        </select>
        <input aria-label="Search" placeholder="Search name, email, text" className={`${SELECT} min-w-[240px]`}
          value={q} onChange={e => { setQ(e.target.value); reset() }} />
        <span className="label ml-auto">{total} {total === 1 ? 'conversation' : 'conversations'}</span>
      </div>
      <TopicFilter filters={topicFilters} value={topic} onChange={t => { setTopic(t); reset() }} />

      {canAct && picked.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 border border-ink-500 bg-ink-800 px-3 py-2">
          <span className="label-amber">{picked.size} selected</span>
          <select aria-label="Assign to" className={SELECT} value="" disabled={bulk.isPending}
            onChange={e => e.target.value && bulk.mutate({ action: 'assign', assignee_user_id: e.target.value === 'none' ? null : Number(e.target.value) })}>
            <option value="">Assign…</option>
            <option value="none">Unassigned</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.email}</option>)}
          </select>
          <select aria-label="Snooze" className={SELECT} value="" disabled={bulk.isPending}
            onChange={e => e.target.value && snooze(Number(e.target.value))}>
            <option value="">Snooze…</option>
            <option value="1">1 day</option><option value="3">3 days</option><option value="7">1 week</option>
          </select>
          <button className="label hover:underline" disabled={bulk.isPending} onClick={() => bulk.mutate({ action: 'mark_reviewed' })}>Mark reviewed</button>
          <button className="label hover:underline" disabled={bulk.isPending} onClick={() => bulk.mutate({ action: 'archive' })}>Archive</button>
          {bulk.isError && <span className="text-red-400 text-sm">
            {bulk.error instanceof ApiError && bulk.error.status === 404
              ? 'Some conversations changed — refresh and try again' : "Couldn't update. Try again."}
          </span>}
        </div>
      )}

      {list.isLoading && <p className="text-paper-trace">Loading…</p>}
      {list.isError && <p className="text-red-400">Couldn't load conversations. Try again.</p>}
      {list.data && items.length === 0 && <p className="text-paper-trace">Nothing here.</p>}

      {items.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {canAct && <th className="w-8 py-2"><input type="checkbox" aria-label="Select all" checked={allOn}
                onChange={() => setPicked(allOn ? new Set() : new Set(items.map(key)))} /></th>}
              {['Waiting', 'Customer', 'Title', 'Channel', 'Topic', 'State', 'Owner'].map(h => <th key={h} className="label py-2 pr-3 font-normal">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map(c => {
              const tone = c.state === 'to_review' ? waitingTone(c.waiting_hours, c.target_hours) : 'ok'
              return (
                <tr key={key(c)} className="border-t border-ink-500 hover:bg-ink-800">
                  {canAct && <td className="py-2"><input type="checkbox" aria-label={`Select ${c.name || c.email || 'unknown'}, ${c.channel}`} checked={picked.has(key(c))}
                    onChange={() => setPicked(s => { const n = new Set(s); if (n.has(key(c))) n.delete(key(c)); else n.add(key(c)); return n })} /></td>}
                  <td className={`py-2 pr-3 font-mono ${TONE[tone]}`}>{waitingLabel(c.waiting_hours)}</td>
                  <td className="py-2 pr-3">
                    <Link to={conversationHref(c, publication)} className="text-paper hover:underline">{c.name || c.email || 'Unknown'}</Link>
                    <span className="label ml-2">{c.member ? 'Member' : 'Visitor'}</span>
                    <div className="text-paper-trace text-xs truncate max-w-[40ch]">{c.snippet}</div>
                  </td>
                  <td className="py-2 pr-3 text-paper-dim">{TITLE_LABELS[c.publication ?? 'none']}</td>
                  <td className="py-2 pr-3 text-paper-dim capitalize">{c.channel}</td>
                  <td className="py-2 pr-3"><Chip tone={topicTone(c.topic)}>{topicLabel(c.topic)}</Chip></td>
                  <td className="py-2 pr-3 text-paper-dim">{stateLabel(c.state)}</td>
                  <td className="py-2 text-paper-dim">{c.assignee_email ?? '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {total > PAGE && (
        <div className="flex gap-4 items-center">
          <button className="label hover:underline disabled:opacity-40" disabled={offset === 0} onClick={() => { setOffset(Math.max(0, offset - PAGE)); setPicked(new Set()) }}>Previous</button>
          <span className="label">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <button className="label hover:underline disabled:opacity-40" disabled={offset + PAGE >= total} onClick={() => { setOffset(offset + PAGE); setPicked(new Set()) }}>Next</button>
        </div>
      )}
    </section>
  )
}

export function deskOff(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404
}
